import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { filesURL } from '../../../constants/auth';
import Modal from '../../../componentsv2/Modal';
import Typography from '../../../componentsv2/Typography';
import Button from '../../../componentsv2/Button';
import Form from '../../../componentsv2/Form';
import { TextField } from '../../../componentsv2/Field';
import { ArrowRightIcon } from '../../../components/Icons';
import SatConnectionIcon from './SatConnectionIcon';
import SyntageSecureIcon from './SyntageSecureIcon';
import SyntagePrivateIcon from './SyntagePrivateIcon';
import { useTranslation } from '../../../hooks/i18n';
import { useSnackbar } from '../../../context/snackbar';
import { COLORS } from '../../../themev2';
import { createSatCredential } from '../../../services/company';

interface SyntangeConnectionProps {
  onBack: () => void;
  onNext: () => void;
  isLoading?: boolean;
  companyId?: number;
  initialRfc?: string;
}

const SyntangeConnection = ({
  onBack,
  onNext,
  isLoading,
  companyId,
  initialRfc,
}: SyntangeConnectionProps) => {
  const { t } = useTranslation(['activation']);
  const { showSnackbar } = useSnackbar();

  const credentialValidationSchema = Yup.object({
    rfc: Yup.string().required(t('common:requiredField')),
    password: Yup.string().required(t('common:requiredField')),
  });
  const [showForm, setShowForm] = useState(Boolean(initialRfc));
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tab, setTab] = useState(0);

  const handleSubmitCredentials = async (rfc: string, password: string) => {
    if (!companyId) return;
    setIsConnecting(true);
    try {
      await createSatCredential(companyId, rfc, password);
      showSnackbar(t('activation:step2mx.successTitle'), 'success');
      setIsSuccess(true);
      onNext();
    } catch (error) {
      setIsConnecting(false);
      showSnackbar(t('activation:step2mx.connectionError'), 'error');
    }
  };

  return (
    <Modal isOpen handleClose={onBack} size="medium" displayCloseButton={!isSuccess} isAutoHeight>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
        {/* Intro view */}
        {!isSuccess && !showForm && (
          <>
            <SatConnectionIcon />

            <Typography
              sx={{ fontWeight: 600, fontSize: '22px', color: COLORS.brand.green, textAlign: 'center' }}
            >
              {t('activation:step2mx.title')}
            </Typography>

            <Typography sx={{ fontSize: '14px', color: COLORS.gray[1100], textAlign: 'center' }}>
              {t('activation:step2mx.subtitle')}
            </Typography>

            <Divider sx={{ width: '100%', my: 1 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    background: COLORS.brand.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: COLORS.secondary.indigo,
                  }}
                >
                  <SyntageSecureIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: COLORS.brand.night }}>
                    {t('activation:step2mx.secureTitle')}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: COLORS.gray[1100], mt: '2px' }}>
                    {t('activation:step2mx.secureDescription')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    background: COLORS.brand.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <SyntagePrivateIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: COLORS.brand.night }}>
                    {t('activation:step2mx.privateTitle')}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: COLORS.gray[1100], mt: '2px' }}>
                    {t('activation:step2mx.privateDescription')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ width: '100%', my: 1 }} />

            <Button
              color="secondary"
              onClick={() => setShowForm(true)}
              isLoading={isLoading}
              endIcon={<ArrowRightIcon />}
            >
              {t('activation:step2mx.connectButton')}
            </Button>

            <Typography sx={{ fontSize: '12px', color: COLORS.gray[1000], textAlign: 'center' }}>
              {t('activation:step2mx.privacyText1')}{' '}
              <Box
                component="a"
                href={`${filesURL}/MarcoTerms&Conditions.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: COLORS.brand.green, textDecoration: 'underline' }}
              >
                Marco
              </Box>{' '}
              {t('activation:step2mx.privacyText2')}{' '}
              <Box
                component="a"
                href="https://syntage.com/terminos-y-condiciones"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: COLORS.brand.green, textDecoration: 'underline' }}
              >
                SAT's
              </Box>{' '}
              {t('activation:step2mx.privacyText3')}
            </Typography>
          </>
        )}

        {/* Credentials form view */}
        {!isSuccess && showForm && (
          <>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '22px',
                color: COLORS.brand.green,
                textAlign: 'center',
                mt: 1,
              }}
            >
              {t('activation:step2mx.enterCredentials')}
            </Typography>

            <Typography sx={{ fontSize: '13px', color: COLORS.gray[1100], textAlign: 'center', mb: 1 }}>
              {t('activation:step2mx.enterCredentialsInformation')}
            </Typography>

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                width: '100%',
                '& .MuiTabs-indicator': { backgroundColor: COLORS.secondary.indigo },
                '& .MuiTab-root': { color: COLORS.gray[1100], textTransform: 'none', fontWeight: 500 },
                '& .Mui-selected': { color: `${COLORS.secondary.indigo} !important` },
              }}
            >
              <Tab label="CIEC" />
            </Tabs>

            <Formik
              initialValues={{ rfc: initialRfc ?? '', password: '' }}
              validationSchema={credentialValidationSchema}
              onSubmit={(values) => handleSubmitCredentials(values.rfc, values.password)}
            >
              {({ submitForm }) => (
                <Form name="credentials-form" id="credentials-form">
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      width: '100%',
                      minWidth: '360px',
                    }}
                  >
                    <TextField name="rfc" label="RFC" placeholder="RFC" />
                    <TextField name="password" label="Password" placeholder="Password" type="password" />
                    <Button color="secondary" onClick={submitForm} isLoading={isConnecting}>
                      {t('activation:step2mx.accept')}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default SyntangeConnection;
